// ABOUTME: Main report editor component with section editing, Magic Edit, AI Edit, and toolbar.
// ABOUTME: Handles optimistic locking (versionRef), auto-save, section management.
// ABOUTME: Integrates FullAIEditPanel (per-section apply, bulk apply, undo with toast).
// ABOUTME: Integrates AddInsightPanel (pinned/all insights, drag-and-drop, auto-pin for unpinned).
// ABOUTME: Drop handlers support both INSIGHT_DRAG_TYPE (pin IDs) and INSIGHT_REF_DRAG_TYPE (unpinned refs).

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDocumentHistory } from '@/hooks/useDocumentHistory';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save,
  Download,
  Share,
  Plus,
  Loader2,
  Check,
  AlertTriangle,
  Type,
  Heading1,
  BarChart3,
  Table,
  Undo,
  Redo,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Highlighter,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  RemoveFormatting,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Pencil,
  RefreshCw,
  Pin,
  X,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { Editor } from '@tiptap/react';
import { MagicEditOverlay } from './MagicEditOverlay';
import { ShareReportModal } from './ShareReportModal';
import { FullAIEditPanel, type ToneValue } from './FullAIEditPanel';
import { OleumIcon } from '@/components/icons/OleumIcon';
import { AddInsightPanel, INSIGHT_DRAG_TYPE, INSIGHT_REF_DRAG_TYPE } from './AddInsightPanel';
import { UnifiedReportContent } from './UnifiedReportContent';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { FontSizeDropdown } from '@/components/ui/FontSizeDropdown';
import { apiClient, ApiError } from '@/services/api';
import { useApp } from '@/context';
import type { Report, ReportSection, MagicEditResponse, FullReportEditSuggestion } from '@/types/api';

interface TextSelection {
  sectionId: string;
  startOffset: number;
  endOffset: number;
  selectedText: string;
  rect?: DOMRect;
}

interface ReportEditorProps {
  report: Report;
  onSave: (report: Report) => void;
  onBack: () => void;
  onRegenerate?: () => void;
}

/** Relative time display that self-updates every 30 seconds */
function TimeAgo({ date }: { date: Date }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 30) return <>Saved just now</>;
  if (seconds < 90) return <>Saved 1m ago</>;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return <>Saved {minutes}m ago</>;
  return <>Saved {Math.floor(minutes / 60)}h ago</>;
}

export function ReportEditor({ report: initialReport, onSave, onBack, onRegenerate }: ReportEditorProps) {
  const { currentWorkspaceId, userProfileId } = useApp();

  const [report, setReport] = useState<Report>(initialReport);
  const [sections, setSections] = useState<ReportSection[]>(initialReport.sections);
  const [version, setVersion] = useState(initialReport.version);

  // Use ref for version to avoid stale closure in auto-save
  const versionRef = useRef(initialReport.version);

  // Keep versionRef in sync with version state
  useEffect(() => {
    versionRef.current = version;
  }, [version]);

  // Save state
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [conflictState, setConflictState] = useState<{ latestReport: Report } | null>(null);

  // Magic Edit state
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const [magicEditActive, setMagicEditActive] = useState(false);
  const [magicEditLoading, setMagicEditLoading] = useState(false);
  const [magicSuggestion, setMagicSuggestion] = useState<string | null>(null);
  const [pendingMagicEdit, setPendingMagicEdit] = useState<MagicEditResponse | null>(null);

  // UI state
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState(initialReport.title);

  // Full AI Edit state
  const [showAIEditPanel, setShowAIEditPanel] = useState(false);
  const [aiEditLoading, setAiEditLoading] = useState(false);
  const [aiEditSuggestion, setAiEditSuggestion] = useState<FullReportEditSuggestion | null>(null);
  const [aiEditError, setAiEditError] = useState<string | null>(null);

  // Undo state for AI edits
  const [preEditSections, setPreEditSections] = useState<ReportSection[] | null>(null);
  const [preEditSuggestion, setPreEditSuggestion] = useState<FullReportEditSuggestion | null>(null);
  const [undoToast, setUndoToast] = useState<string | null>(null);
  const [lastAppliedSectionId, setLastAppliedSectionId] = useState<string | null>(null);
  // Signal object: when this changes, the panel restores the section. Uses a new object ref each time to guarantee useEffect fires.
  const [undoSignal, setUndoSignal] = useState<{ sectionId: string } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks which section IDs have been individually applied via AI edit (for document-undo detection)
  const appliedAISectionIdsRef = useRef<Set<string>>(new Set());
  // Pre-filled prompt when opening AI edit from text selection
  const [aiEditInitialPrompt] = useState('');

  // Cleanup undo timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // Add Insight state
  const [showAddInsightPanel, setShowAddInsightPanel] = useState(false);
  const [addInsightLoading, setAddInsightLoading] = useState(false);
  const [addInsightError, setAddInsightError] = useState<string | null>(null);
  const [addInsightSuccess, setAddInsightSuccess] = useState<string | null>(null);

  // Drop zone state for drag-and-drop from pinboard
  const [isDragOver, setIsDragOver] = useState(false);

  // Sync editingTitle with report.title when not editing
  useEffect(() => {
    if (!isEditingTitle) {
      setEditingTitle(report.title);
    }
  }, [report.title, isEditingTitle]);

  // Auto-save debounce
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUnsavedChanges = useRef(false);

  // Track changed sections for individual updates
  const changedSectionsRef = useRef<Map<string, string>>(new Map());

  // When set, handleSave will replace all sections instead of patching individual ones
  const pendingFullSectionsRef = useRef<ReportSection[] | null>(null);

  // Document-level undo/redo
  const history = useDocumentHistory();
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  // Active editor for formatting toolbar (set by UnifiedReportContent)
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);

  // Force re-render when editor state changes (for toolbar button states)
  const [editorUpdateTrigger, setEditorUpdateTrigger] = useState(0);

  // Ref to the editor container for selection constraint
  const editorContainerRef = useRef<HTMLDivElement>(null);

  // Subscribe to active editor updates to trigger toolbar re-renders
  useEffect(() => {
    if (!activeEditor) return;

    const updateHandler = () => {
      setEditorUpdateTrigger(prev => prev + 1);
    };

    // Listen to editor updates (cursor moves, mark changes, etc.)
    activeEditor.on('update', updateHandler);
    activeEditor.on('selectionUpdate', updateHandler);
    activeEditor.on('transaction', updateHandler);

    return () => {
      activeEditor.off('update', updateHandler);
      activeEditor.off('selectionUpdate', updateHandler);
      activeEditor.off('transaction', updateHandler);
    };
  }, [activeEditor]);

  // Auto-save effect — paused while a conflict is being resolved
  useEffect(() => {
    if (hasUnsavedChanges.current && !conflictState) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 2000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [sections, conflictState]);

  // Constrain selection to single section for Magic Edit
  useEffect(() => {
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;

      // Only check if within our editor container
      if (!editorContainerRef.current) return;

      const range = sel.getRangeAt(0);

      // Find the section elements containing the start and end of selection
      const findSectionElement = (node: Node): HTMLElement | null => {
        let current: Node | null = node;
        while (current) {
          if (current instanceof HTMLElement && current.hasAttribute('data-section-id')) {
            return current;
          }
          current = current.parentNode;
        }
        return null;
      };

      const startSection = findSectionElement(range.startContainer);
      const endSection = findSectionElement(range.endContainer);

      // If selection spans multiple sections, clear it
      if (startSection && endSection && startSection !== endSection) {
        sel.removeAllRanges();
        // Clear any pending Magic Edit state
        setSelection(null);
        setMagicSuggestion(null);
        setPendingMagicEdit(null);
      }
    };

    // Use a debounced version to avoid excessive calls
    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedHandler = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleSelectionChange, 10);
    };

    document.addEventListener('selectionchange', debouncedHandler);

    return () => {
      document.removeEventListener('selectionchange', debouncedHandler);
      clearTimeout(timeoutId);
    };
  }, []);

  // Save report by updating individual changed sections
  const handleSave = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3;

    if (saveStatus === 'saving' && retryCount === 0) return;
    if (!currentWorkspaceId) {
      console.error('Workspace not available for save');
      return;
    }

    // Structural change (section add/delete undo) — replace full sections array
    if (pendingFullSectionsRef.current) {
      const sections = pendingFullSectionsRef.current;
      pendingFullSectionsRef.current = null;
      setSaveStatus('saving');
      try {
        const updatedReport = await apiClient.reports.replaceSections(
          report.id,
          sections,
          currentWorkspaceId,
          versionRef.current,
        );
        setVersion(updatedReport.version);
        versionRef.current = updatedReport.version;
        setReport(updatedReport);
        changedSectionsRef.current.clear();
        setSaveStatus('saved');
        setLastSavedAt(new Date());
        hasUnsavedChanges.current = false;
        onSave(updatedReport);
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          try {
            const latestReport = await apiClient.reports.get(report.id);
            setSaveStatus('error');
            setConflictState({ latestReport });
          } catch {
            setSaveStatus('error');
          }
        } else {
          setSaveStatus('error');
          console.error('Failed to save sections replacement:', error);
        }
      }
      return;
    }

    const changedSections = changedSectionsRef.current;
    if (changedSections.size === 0) {
      return; // Nothing to save
    }

    setSaveStatus('saving');

    try {
      // Use versionRef to get the latest version, avoiding stale closures
      let currentVersion = versionRef.current;
      let latestReport: Report | undefined;

      // Update each changed section individually
      for (const [sectionId, content] of changedSections.entries()) {
        const updatedReport = await apiClient.reports.updateSection(
          report.id,
          sectionId,
          content,
          currentWorkspaceId,
          currentVersion
        );

        // Update version for next iteration
        currentVersion = updatedReport.version;
        setVersion(currentVersion);
        versionRef.current = currentVersion;
        setReport(updatedReport);
        latestReport = updatedReport;
      }

      // Clear changed sections after successful save
      changedSectionsRef.current.clear();

      setSaveStatus('saved');
      setLastSavedAt(new Date());
      hasUnsavedChanges.current = false;
      onSave(latestReport ?? report);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // Version conflict — surface to user immediately
        try {
          const latestReport = await apiClient.reports.get(report.id);
          setSaveStatus('error');
          setConflictState({ latestReport });
        } catch {
          // Can't even fetch latest — show generic error
          setSaveStatus('error');
        }
      } else if (error instanceof ApiError && error.status >= 500) {
        // Server error - retry with exponential backoff
        if (retryCount < MAX_RETRIES) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          console.log(`Server error, retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
          setTimeout(() => handleSave(retryCount + 1), delay);
          return;
        }
        setSaveStatus('error');
        console.error('Failed to save report after retries:', error);
      } else {
        setSaveStatus('error');
        console.error('Failed to save report:', error);
      }
    }
  }, [report.id, onSave, saveStatus, currentWorkspaceId]);

  // Conflict resolution: keep local changes, force-save with latest version
  const handleKeepMyChanges = useCallback(async () => {
    if (!conflictState) return;
    setVersion(conflictState.latestReport.version);
    versionRef.current = conflictState.latestReport.version;
    setConflictState(null);
    setSaveStatus('saved');
    await handleSave();
  }, [conflictState, handleSave]);

  // Conflict resolution: discard local changes, load server state
  const handleLoadTheirChanges = useCallback(() => {
    if (!conflictState) return;
    setReport(conflictState.latestReport);
    setSections(conflictState.latestReport.sections);
    setVersion(conflictState.latestReport.version);
    versionRef.current = conflictState.latestReport.version;
    changedSectionsRef.current.clear();
    hasUnsavedChanges.current = false;
    setConflictState(null);
    setSaveStatus('saved');
    setLastSavedAt(new Date());
  }, [conflictState]);

  // Conflict resolution: dismiss banner, adopt server version, and resume auto-save
  const handleDismissConflict = useCallback(() => {
    if (!conflictState) return;
    // Adopt the server version so the next auto-save won't hit 409 again
    setVersion(conflictState.latestReport.version);
    versionRef.current = conflictState.latestReport.version;
    setConflictState(null);
    setSaveStatus('saved');
  }, [conflictState]);

  // Refetch report after insight_reference conversion or visualization update
  const refetchReport = useCallback(async () => {
    try {
      const updatedReport = await apiClient.reports.get(report.id);
      setReport(updatedReport);
      setSections(updatedReport.sections);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
    } catch (error) {
      console.error('Failed to refetch report:', error);
    }
  }, [report.id]);

  // Update section content
  const updateSectionContent = useCallback((sectionId: string, content: string) => {
    history.captureBeforeTextEdit(sectionsRef.current);

    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, content } : s))
    );

    // Track this section as changed
    changedSectionsRef.current.set(sectionId, content);
    hasUnsavedChanges.current = true;
  }, [history]);

  // Delete section
  const deleteSection = useCallback(async (sectionId: string) => {
    if (!userProfileId) return;
    history.captureBeforeStructuralChange(sectionsRef.current);

    try {
      const updatedReport = await apiClient.reports.deleteSection(
        report.id,
        sectionId,
        userProfileId,
        versionRef.current
      );
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      setReport(updatedReport);
      setSections(updatedReport.sections);
      changedSectionsRef.current.delete(sectionId);
    } catch (error) {
      console.error('Failed to delete section:', error);
      setSaveStatus('error');
    }
  }, [report.id, userProfileId]);

  // Add new section
  const addSection = useCallback(async (type: ReportSection['type'], afterSectionId?: string) => {
    if (!userProfileId) return;
    history.captureBeforeStructuralChange(sectionsRef.current);

    // Default content based on section type
    let defaultContent = 'Enter content here...';
    if (type === 'heading') {
      defaultContent = 'New Section';
    } else if (type === 'visualization') {
      defaultContent = JSON.stringify({
        type: 'bar',
        title: 'Chart Title',
        data: [
          { label: 'Jan', value: 100 },
          { label: 'Feb', value: 150 },
          { label: 'Mar', value: 120 },
          { label: 'Apr', value: 180 },
          { label: 'May', value: 200 },
        ],
      });
    } else if (type === 'table') {
      defaultContent = JSON.stringify({
        title: 'Table Title',
        headers: ['Column 1', 'Column 2', 'Column 3'],
        rows: [
          ['Row 1 Data', 100, 'Value'],
          ['Row 2 Data', 200, 'Value'],
          ['Row 3 Data', 300, 'Value'],
        ],
      });
    }

    const newSection: ReportSection = {
      id: `section_${Date.now()}`,
      type,
      content: defaultContent,
      order: sections.length,
    };

    try {
      // Immediately persist to backend
      const updatedReport = await apiClient.reports.addSection(
        report.id,
        newSection,
        userProfileId,
        versionRef.current
      );
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      setReport(updatedReport);
      setSections(updatedReport.sections);
    } catch (error) {
      console.error('Failed to add section:', error);
      // Optimistically update UI anyway
      setSections((prev) => {
        if (afterSectionId) {
          const index = prev.findIndex((s) => s.id === afterSectionId);
          const updated = [...prev];
          updated.splice(index + 1, 0, newSection);
          return updated.map((s, i) => ({ ...s, order: i }));
        }
        return [...prev, newSection];
      });
    }
  }, [sections.length, report.id, userProfileId]);

  // Magic Edit: Submit prompt
  const handleMagicEditSubmit = useCallback(async (prompt: string) => {
    if (!selection) return;

    setMagicEditLoading(true);

    try {
      const response = await apiClient.reports.magicEdit(report.id, {
        section_id: selection.sectionId,
        prompt,
        start_offset: selection.startOffset,
        end_offset: selection.endOffset,
      });

      setMagicSuggestion(response.suggested_text);
      setPendingMagicEdit(response);
    } catch (error) {
      console.error('Magic Edit failed:', error);
    } finally {
      setMagicEditLoading(false);
    }
  }, [selection, report.id]);

  // Magic Edit: Apply suggestion
  const handleMagicEditApply = useCallback(() => {
    if (!pendingMagicEdit || !selection) return;
    history.captureBeforeStructuralChange(sectionsRef.current);

    setSections((prev) =>
      prev.map((s) => {
        if (s.id === selection.sectionId && s.content) {
          const before = s.content.slice(0, pendingMagicEdit.selection_start);
          const after = s.content.slice(pendingMagicEdit.selection_end);
          const newContent = before + pendingMagicEdit.suggested_text + after;

          // Track this section as changed
          changedSectionsRef.current.set(s.id, newContent);

          return { ...s, content: newContent };
        }
        return s;
      })
    );

    hasUnsavedChanges.current = true;
    setSelection(null);
    setMagicSuggestion(null);
    setPendingMagicEdit(null);
  }, [pendingMagicEdit, selection]);

  // Magic Edit: Reject/try again
  const handleMagicEditReject = useCallback(() => {
    setMagicSuggestion(null);
    setPendingMagicEdit(null);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelection(null);
    setMagicEditActive(false);
    setMagicSuggestion(null);
    setPendingMagicEdit(null);
  }, []);

  // PDF export state
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Export PDF - calls backend which uses Playwright for pixel-perfect rendering
  const handleExportPdf = useCallback(async () => {
    setIsExportingPdf(true);
    try {
      const pdfBlob = await apiClient.reports.getPdf(report.id);

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setSaveStatus('error');
    } finally {
      setIsExportingPdf(false);
    }
  }, [report.id, report.title]);

  // Save title
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  const handleTitleSave = useCallback(async () => {
    // Prevent double-save
    if (isSavingTitle) return;

    const trimmedTitle = editingTitle.trim();

    // If no change or empty, just close edit mode
    if (!trimmedTitle || trimmedTitle === report.title) {
      setEditingTitle(report.title);
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);
    try {
      const updatedReport = await apiClient.reports.update(
        report.id,
        { title: trimmedTitle },
        versionRef.current
      );

      // Update all state
      setReport(updatedReport);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      setEditingTitle(updatedReport.title);
      setIsEditingTitle(false);

      // Notify parent
      onSave(updatedReport);
    } catch (error) {
      console.error('[ReportEditor] Failed to update title:', error);
      // Reset to current title on error
      setEditingTitle(report.title);
      setIsEditingTitle(false);

      // Show error feedback
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('saved'), 3000);
    } finally {
      setIsSavingTitle(false);
    }
  }, [editingTitle, report.id, report.title, onSave, isSavingTitle]);

  const handleTitleCancel = useCallback(() => {
    setEditingTitle(report.title);
    setIsEditingTitle(false);
  }, [report.title]);

  // Full AI Edit handlers
  const handleAIEditSubmit = useCallback(async (prompt: string, tone: string) => {
    setAiEditLoading(true);
    setAiEditError(null);
    try {
      const suggestion = await apiClient.reports.requestFullEdit(report.id, {
        prompt,
        tone: tone as ToneValue,
      });
      setAiEditSuggestion(suggestion);
      appliedAISectionIdsRef.current.clear();
    } catch (error) {
      console.error('Full AI edit failed:', error);
      setAiEditError(
        error instanceof ApiError
          ? error.message
          : 'Failed to generate edit suggestions. Please try again.',
      );
    } finally {
      setAiEditLoading(false);
    }
  }, [report.id]);

  const handleAIEditApplySelected = useCallback(async (acceptedSectionIds: Set<string>, applyReorder?: boolean) => {
    if (!aiEditSuggestion) return;
    history.captureBeforeStructuralChange(sectionsRef.current);

    // Store pre-edit state for undo
    const savedSections = [...sections];

    const deletedIds = new Set(aiEditSuggestion.deleted_section_ids || []);
    const originals = aiEditSuggestion.original_sections || [];
    const suggested = aiEditSuggestion.suggested_sections || [];

    // If applying reorder, use suggested sections' order as the base
    // Build a map of suggested order by ID
    const suggestedOrderMap = new Map<string, number>();
    if (applyReorder) {
      for (const s of suggested) {
        suggestedOrderMap.set(s.id, s.order ?? 0);
      }
    }

    // Build merged section list: accepted changes use suggested, rejected keep original
    const originalIds = new Set(originals.map((o) => o.id));
    const mergedSections: ReportSection[] = [];

    for (const orig of originals) {
      const id = orig.id;
      if (deletedIds.has(id)) {
        if (acceptedSectionIds.has(id)) continue; // Accept deletion: omit
        mergedSections.push(orig); // Reject deletion: keep original
        continue;
      }
      if (acceptedSectionIds.has(id)) {
        const sugg = suggested.find((s) => s.id === id);
        mergedSections.push(sugg || orig);
      } else {
        // Not accepted for content change — but may need reorder
        const section = { ...orig };
        if (applyReorder && suggestedOrderMap.has(id)) {
          section.order = suggestedOrderMap.get(id)!;
        }
        mergedSections.push(section);
      }
    }

    // Include accepted new sections (in suggested but not in originals)
    for (const sugg of suggested) {
      if (!originalIds.has(sugg.id) && acceptedSectionIds.has(sugg.id)) {
        mergedSections.push(sugg);
      }
    }

    // Build suggested order map for sorting
    const suggestedOrderForSort = new Map<string, number>();
    for (const s of suggested) {
      suggestedOrderForSort.set(s.id, s.order ?? 0);
    }


    // Sort: use suggested order for new sections (needed for placement) or when reorder is accepted
    mergedSections.sort((a, b) => {
      const aNew = !originalIds.has(a.id);
      const bNew = !originalIds.has(b.id);
      if (aNew || bNew || applyReorder) {
        const aOrder = suggestedOrderForSort.get(a.id) ?? a.order ?? 0;
        const bOrder = suggestedOrderForSort.get(b.id) ?? b.order ?? 0;
        return aOrder - bOrder;
      }
      return (a.order ?? 0) - (b.order ?? 0);
    });
    // Renumber sequentially
    mergedSections.forEach((s, i) => { s.order = i; });

    try {
      setAiEditError(null);
      const updatedReport = await apiClient.reports.applyFullEdit(
        report.id,
        mergedSections,
        aiEditSuggestion.prompt_used,
        versionRef.current,
      );
      setReport(updatedReport);
      setSections(updatedReport.sections);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      // Clear pending auto-save — the full edit apply sent the definitive state to the server
      changedSectionsRef.current.clear();
      hasUnsavedChanges.current = false;
      // Save suggestion so undo can restore it
      setPreEditSuggestion(aiEditSuggestion);
      setAiEditSuggestion(null);
      appliedAISectionIdsRef.current.clear();
      // Don't close panel — user can make another edit or close manually

      // Enable undo
      setPreEditSections(savedSections);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoToast('AI edits applied');
      undoTimerRef.current = setTimeout(() => {
        setUndoToast(null);
        setPreEditSections(null);
        setPreEditSuggestion(null);
      }, 10000);
    } catch (error) {
      console.error('Failed to apply AI edit:', error);
      if (error instanceof ApiError && error.status === 409) {
        // Version conflict - try to recover
        try {
          const latestReport = await apiClient.reports.get(report.id);
          setVersion(latestReport.version);
          versionRef.current = latestReport.version;
          setAiEditError('Version conflict detected. Please try applying again.');
        } catch {
          setAiEditError('Version conflict. Please refresh the page.');
        }
      } else {
        setAiEditError(
          error instanceof ApiError
            ? error.message
            : 'Failed to apply changes. Please try again.',
        );
      }
    }
  }, [report.id, aiEditSuggestion, sections]);

  // Apply a single section's AI suggestion without closing the panel
  const handleAIEditApplySingle = useCallback(async (sectionId: string) => {
    if (!aiEditSuggestion) return;
    history.captureBeforeStructuralChange(sectionsRef.current);

    // Store pre-edit state for undo
    const savedSections = [...sections];

    const deletedIds = new Set(aiEditSuggestion.deleted_section_ids || []);
    const originals = aiEditSuggestion.original_sections || [];
    const suggested = aiEditSuggestion.suggested_sections || [];

    // Build section list: apply only the specified section, keep everything else as-is
    const mergedSections: ReportSection[] = [];

    for (const orig of originals) {
      const id = orig.id;
      if (id === sectionId) {
        if (deletedIds.has(id)) continue; // This section is being deleted — omit it
        const sugg = suggested.find((s) => s.id === id);
        mergedSections.push(sugg || orig);
      } else {
        // For other sections, use current state (which may include previously applied singles)
        const currentSection = sections.find((s) => s.id === id);
        if (currentSection) mergedSections.push(currentSection);
        else if (!deletedIds.has(id)) mergedSections.push(orig);
      }
    }

    // Include previously applied new sections from current state
    const originalIds = new Set(originals.map((o) => o.id));
    for (const s of sections) {
      if (!originalIds.has(s.id) && s.id !== sectionId) {
        mergedSections.push(s);
      }
    }

    // If the applied section is a new one (not in originals), add it
    if (!originalIds.has(sectionId)) {
      const newSugg = suggested.find((s) => s.id === sectionId);
      if (newSugg) mergedSections.push(newSugg);
    }

    // Sort: use suggested order only for new sections (to slot them in correctly),
    // preserve original order for existing sections to avoid implicit reordering
    const suggestedOrderForSort = new Map<string, number>();
    for (const s of suggested) {
      suggestedOrderForSort.set(s.id, s.order ?? 0);
    }
    mergedSections.sort((a, b) => {
      const aIsNew = !originalIds.has(a.id);
      const bIsNew = !originalIds.has(b.id);
      if (aIsNew || bIsNew) {
        const aOrder = suggestedOrderForSort.get(a.id) ?? a.order ?? 0;
        const bOrder = suggestedOrderForSort.get(b.id) ?? b.order ?? 0;
        return aOrder - bOrder;
      }
      return (a.order ?? 0) - (b.order ?? 0);
    });
    mergedSections.forEach((s, i) => { s.order = i; });

    try {
      setAiEditError(null);
      const updatedReport = await apiClient.reports.applyFullEdit(
        report.id,
        mergedSections,
        aiEditSuggestion.prompt_used,
        versionRef.current,
      );
      setReport(updatedReport);
      setSections(updatedReport.sections);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      // Clear pending auto-save — the full edit apply sent the definitive state to the server
      changedSectionsRef.current.clear();
      hasUnsavedChanges.current = false;

      // Enable undo for single apply
      setPreEditSections(savedSections);
      setLastAppliedSectionId(sectionId);
      appliedAISectionIdsRef.current.add(sectionId);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoToast('Change applied');
      undoTimerRef.current = setTimeout(() => {
        setUndoToast(null);
        setPreEditSections(null);
        setLastAppliedSectionId(null);
      }, 10000);
      // Don't close panel or clear suggestion — let user continue applying others
    } catch (error) {
      console.error('Failed to apply single AI edit:', error);
      if (error instanceof ApiError && error.status === 409) {
        try {
          const latestReport = await apiClient.reports.get(report.id);
          setVersion(latestReport.version);
          versionRef.current = latestReport.version;
          setAiEditError('Version conflict detected. Please try applying again.');
        } catch {
          setAiEditError('Version conflict. Please refresh the page.');
        }
      } else {
        setAiEditError(
          error instanceof ApiError
            ? error.message
            : 'Failed to apply changes. Please try again.',
        );
      }
    }
  }, [report.id, aiEditSuggestion, sections]);

  const handleUndoAIEdit = useCallback(async () => {
    if (!preEditSections) return;
    // Immediately clear the timer to prevent it from nullifying lastAppliedSectionId
    // during the async API call below
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    // Capture the section ID now before any async work or state changes
    const sectionIdToRestore = lastAppliedSectionId;
    try {
      const updatedReport = await apiClient.reports.applyFullEdit(
        report.id,
        preEditSections,
        'undo',
        versionRef.current,
      );
      setReport(updatedReport);
      setSections(updatedReport.sections);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      // Clear pending auto-save — the undo sent the definitive state to the server
      changedSectionsRef.current.clear();
      hasUnsavedChanges.current = false;
      setUndoToast(null);
      setPreEditSections(null);
      // Restore suggestion to the diff pane
      if (preEditSuggestion) {
        setAiEditSuggestion(preEditSuggestion);
        setPreEditSuggestion(null);
      }
      // Signal the panel to restore the section in the diff list.
      // undoSignal carries the section ID directly — no separate prop + setTimeout dance.
      if (sectionIdToRestore) {
        appliedAISectionIdsRef.current.delete(sectionIdToRestore);
        setUndoSignal({ sectionId: sectionIdToRestore });
      }
    } catch (error) {
      console.error('Failed to undo AI edit:', error);
    }
  }, [report.id, preEditSections, preEditSuggestion, lastAppliedSectionId]);

  const handleAIEditRejectAll = useCallback(() => {
    setAiEditSuggestion(null);
    setAiEditError(null);
    appliedAISectionIdsRef.current.clear();
  }, []);

  // Add Insight handler — accepts pin IDs (PinnedItem.id = Pin MongoDB _id)
  const handleAddInsights = useCallback(async (pinIds: string[], insertAtOrder?: number) => {
    if (!currentWorkspaceId) {
      setAddInsightError('No workspace selected.');
      return;
    }
    if (pinIds.length === 0) return;
    setAddInsightLoading(true);
    setAddInsightError(null);
    setAddInsightSuccess(null);
    try {
      const updatedReport = await apiClient.reports.addInsightsToReport(
        report.id,
        pinIds,
        currentWorkspaceId,
        versionRef.current,
        insertAtOrder,
      );
      setReport(updatedReport);
      setSections(updatedReport.sections);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      setShowAddInsightPanel(false);
      setAddInsightError(null);
      // Show success toast
      const msg = pinIds.length === 1 ? 'Insight added to report' : `${pinIds.length} insights added to report`;
      setAddInsightSuccess(msg);
      setTimeout(() => setAddInsightSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to add insights:', error);
      if (error instanceof ApiError) {
        setAddInsightError(`Failed to add insights: ${error.message}`);
      } else {
        setAddInsightError('Failed to add insights. Please try again.');
      }
      throw error; // Re-throw so AddInsightPanel's handleAdd knows it failed
    } finally {
      setAddInsightLoading(false);
    }
  }, [report.id, currentWorkspaceId]);

  // Add an unpinned insight by auto-pinning it first, then adding to report
  const handleAddInsightByRef = useCallback(async (messageId: string, insightId: string) => {
    if (!currentWorkspaceId) {
      setAddInsightError('No workspace selected.');
      return;
    }
    setAddInsightLoading(true);
    setAddInsightError(null);
    try {
      // Step 1: Pin the insight
      const pin = await apiClient.pins.create({ message_id: messageId, insight_id: insightId });
      // Step 2: Add the pin to the report
      const updatedReport = await apiClient.reports.addInsightsToReport(
        report.id,
        [pin.id],
        currentWorkspaceId,
        versionRef.current,
      );
      setReport(updatedReport);
      setSections(updatedReport.sections);
      setVersion(updatedReport.version);
      versionRef.current = updatedReport.version;
      setAddInsightSuccess('Insight pinned and added to report');
      setTimeout(() => setAddInsightSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to add insight by ref:', error);
      if (error instanceof ApiError) {
        setAddInsightError(`Failed to add insight: ${error.message}`);
      } else {
        setAddInsightError('Failed to add insight. Please try again.');
      }
    } finally {
      setAddInsightLoading(false);
    }
  }, [report.id, currentWorkspaceId]);

  // Drag-and-drop handlers for insight cards from AddInsightPanel
  const handleEditorDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(INSIGHT_DRAG_TYPE) || e.dataTransfer.types.includes(INSIGHT_REF_DRAG_TYPE)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  }, []);

  const handleEditorDragLeave = useCallback((e: React.DragEvent) => {
    // Only hide drop zone when leaving the editor container entirely
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const { clientX, clientY } = e;
    if (
      clientX <= rect.left ||
      clientX >= rect.right ||
      clientY <= rect.top ||
      clientY >= rect.bottom
    ) {
      setIsDragOver(false);
    }
  }, []);

  // Drop handler for positional drop zones between sections
  const handleInsightDrop = useCallback(async (pinId: string, insertAtOrder: number) => {
    setIsDragOver(false);
    // Check if this is an unpinned insight ref (prefixed by UnifiedReportContent)
    if (pinId.startsWith('__ref__')) {
      try {
        const { messageId, insightId } = JSON.parse(pinId.slice(7));
        await handleAddInsightByRef(messageId, insightId);
      } catch (err) {
        console.error('Failed to parse insight ref:', err);
      }
      return;
    }
    await handleAddInsights([pinId], insertAtOrder);
  }, [handleAddInsights, handleAddInsightByRef]);

  // Helper: handle drop of either pinned or unpinned insight
  const handleDropData = useCallback(async (e: React.DragEvent, insertAtOrder?: number) => {
    const pinId = e.dataTransfer.getData(INSIGHT_DRAG_TYPE);
    if (pinId) {
      await handleAddInsights([pinId], insertAtOrder);
      return;
    }
    const refData = e.dataTransfer.getData(INSIGHT_REF_DRAG_TYPE);
    if (refData) {
      try {
        const { messageId, insightId } = JSON.parse(refData);
        await handleAddInsightByRef(messageId, insightId);
      } catch (err) {
        console.error('Failed to parse insight ref drop data:', err);
      }
    }
  }, [handleAddInsights, handleAddInsightByRef]);

  // Fallback: drop on editor container (outside any drop zone) appends at end
  const handleEditorDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    await handleDropData(e);
  }, [handleDropData]);

  // Document-level undo/redo (toolbar buttons)
  const handleDocumentUndo = useCallback(() => {
    const current = sectionsRef.current;
    const prev = history.undo(current);
    if (!prev) return;
    setSections(prev);

    // Detect if this is a structural change (sections added/removed) vs content-only
    const currentIds = new Set(current.map(s => s.id));
    const prevIds = new Set(prev.map(s => s.id));
    const isStructuralChange = currentIds.size !== prevIds.size ||
      [...currentIds].some(id => !prevIds.has(id));

    if (isStructuralChange) {
      // Structural undo: need full sections replacement on the server
      changedSectionsRef.current.clear();
      pendingFullSectionsRef.current = prev;
      hasUnsavedChanges.current = true;
    } else {
      // Content-only undo: use per-section patching
      const currentMap = new Map(current.map(s => [s.id, s.content]));
      changedSectionsRef.current.clear();
      for (const s of prev) {
        if (s.content && currentMap.get(s.id) !== s.content) {
          changedSectionsRef.current.set(s.id, s.content);
        }
      }
      hasUnsavedChanges.current = changedSectionsRef.current.size > 0;
    }

    // If AI edit panel is open, check if this undo reverted an applied AI section.
    // Detect by finding sections that changed AND are in the applied set.
    if (aiEditSuggestion && appliedAISectionIdsRef.current.size > 0) {
      // Check for content changes on applied sections
      for (const appliedId of appliedAISectionIdsRef.current) {
        const prevSection = prev.find(s => s.id === appliedId);
        const currentSection = current.find(s => s.id === appliedId);
        const wasReverted =
          // Content changed back (normal edit revert)
          (prevSection && currentSection && prevSection.content !== currentSection.content) ||
          // Section was deleted by AI apply and undo brought it back
          (prevSection && !currentSection);
        if (wasReverted) {
          appliedAISectionIdsRef.current.delete(appliedId);
          setUndoSignal({ sectionId: appliedId });
          // Clear the toast undo since document undo supersedes it
          if (undoTimerRef.current) {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
          }
          setUndoToast(null);
          setPreEditSections(null);
          break; // One undo step reverts one section
        }
      }
    }
  }, [history, aiEditSuggestion]);

  const handleDocumentRedo = useCallback(() => {
    const current = sectionsRef.current;
    const next = history.redo(current);
    if (!next) return;
    setSections(next);

    const currentIds = new Set(current.map(s => s.id));
    const nextIds = new Set(next.map(s => s.id));
    const isStructuralChange = currentIds.size !== nextIds.size ||
      [...currentIds].some(id => !nextIds.has(id));

    if (isStructuralChange) {
      changedSectionsRef.current.clear();
      pendingFullSectionsRef.current = next;
      hasUnsavedChanges.current = true;
    } else {
      const currentMap = new Map(current.map(s => [s.id, s.content]));
      changedSectionsRef.current.clear();
      for (const s of next) {
        if (s.content && currentMap.get(s.id) !== s.content) {
          changedSectionsRef.current.set(s.id, s.content);
        }
      }
      hasUnsavedChanges.current = changedSectionsRef.current.size > 0;
    }
  }, [history]);

  // Determine if toolbar should be disabled (only during Magic Edit loading)
  const isToolbarDisabled = magicEditLoading;

  // Helper to check if a mark is active (including stored marks for cursor position)
  const isMarkActive = useCallback((markName: string, attributes?: Record<string, any>) => {
    if (!activeEditor) return false;

    // Check if mark is active in current selection
    if (activeEditor.isActive(markName, attributes)) return true;

    // Also check stored marks (marks that will apply to next typed text)
    const { storedMarks } = activeEditor.state;
    if (storedMarks) {
      return storedMarks.some(mark => {
        if (mark.type.name !== markName) return false;
        if (!attributes) return true;
        return Object.keys(attributes).every(key => mark.attrs[key] === attributes[key]);
      });
    }

    return false;
  }, [activeEditor, editorUpdateTrigger]);

  // Formatting toolbar helper
  const ToolbarButton = ({
    onClick,
    isActive,
    disabled,
    title,
    children
  }: {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      onMouseDown={(e) => {
        // Prevent default to stop blur event from firing on the editor
        e.preventDefault();
        if (!disabled) {
          onClick();
        }
      }}
      disabled={disabled}
      className={`p-2 rounded-sm transition-colors cursor-pointer ${
        isActive
          ? 'bg-primary-600 text-white'
          : disabled
            ? 'text-surface-400 cursor-not-allowed'
            : 'text-surface-600 hover:text-surface-800 hover:bg-surface-100'
      }`}
      title={title}
    >
      {children}
    </button>
  );

  // Set of "messageId:insightId" keys for insights already in the report
  const insightKeysInReport = useMemo(() => {
    const keys = new Set<string>();
    for (const s of sections) {
      if (s.type === 'insight_reference' && s.insight_ref) {
        keys.add(`${s.insight_ref.message_id}:${s.insight_ref.insight_id}`);
      }
    }
    return keys;
  }, [sections]);

  // Memoised conflict diff — O(n) via Set lookups, recomputed only when inputs change
  const conflictDiffCount = useMemo(() => {
    if (!conflictState) return 0;
    const remoteSections = conflictState.latestReport.sections || [];
    const remoteMap = new Map(remoteSections.map(s => [s.id, s.content]));
    const localIds = new Set(sections.map(s => s.id));
    const remoteIds = new Set(remoteSections.map(s => s.id));
    const changedCount = sections.filter(s => remoteMap.has(s.id) && remoteMap.get(s.id) !== s.content).length;
    const addedRemote = remoteSections.filter(s => !localIds.has(s.id)).length;
    const removedRemote = sections.filter(s => !remoteIds.has(s.id)).length;
    return changedCount + addedRemote + removedRemote;
  }, [conflictState, sections]);

  return (
    <div className="flex flex-col h-full report-editor-container">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-surface-200">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            Back
          </Button>
          <div className="h-4 w-px bg-surface-200" />

          {/* Inline Editable Title */}
          <div className="relative group">
            {isEditingTitle ? (
              <div className="flex items-center">
                <input
                  type="text"
                  value={editingTitle}
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTitleSave();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleTitleCancel();
                    }
                  }}
                  onBlur={handleTitleSave}
                  autoFocus
                  disabled={isSavingTitle}
                  className="text-lg font-semibold text-surface-800 bg-transparent border-b-2 border-primary-600 px-1 py-0.5 outline-none min-w-[200px] max-w-[400px] transition-all placeholder:text-surface-400 disabled:opacity-50"
                  placeholder="Untitled Report"
                  style={{ width: `${Math.max(200, Math.min(400, editingTitle.length * 10 + 20))}px` }}
                />
                {isSavingTitle && (
                  <Loader2 className="w-4 h-4 ml-2 text-primary-600 animate-spin" />
                )}
              </div>
            ) : (
              <button
                className="flex items-center gap-2 text-lg font-semibold text-surface-800 hover:text-primary-600 transition-colors py-0.5 border-b-2 border-transparent hover:border-primary-600/30"
                onClick={() => setIsEditingTitle(true)}
                title="Click to rename"
              >
                <span className="max-w-[400px] truncate">{report.title}</span>
                <Pencil className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-surface-500" />
              </button>
            )}
          </div>

          {/* Save Status */}
          <div className="flex items-center gap-2 text-sm">
            {saveStatus === 'saving' && (
              <>
                <Loader2 className="w-4 h-4 text-surface-500 animate-spin" />
                <span className="text-surface-500">Saving...</span>
              </>
            )}
            {saveStatus === 'saved' && (
              <>
                <Check className="w-4 h-4 text-success" />
                {lastSavedAt ? (
                  <span className="text-surface-400"><TimeAgo date={lastSavedAt} /></span>
                ) : (
                  <span className="text-surface-500">Saved</span>
                )}
              </>
            )}
            {saveStatus === 'error' && !conflictState && (
              <>
                <AlertTriangle className="w-4 h-4 text-error" />
                <span className="text-error">Error saving</span>
              </>
            )}
            {conflictState && (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-amber-600">Conflict</span>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRegenerate && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              leftIcon={<RefreshCw className="w-4 h-4" />}
            >
              Regenerate
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowAddInsightPanel(false);
              setShowAIEditPanel(!showAIEditPanel);
            }}
            leftIcon={<OleumIcon className="w-4 h-4" />}
          >
            AI Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowAIEditPanel(false);
              setShowAddInsightPanel(!showAddInsightPanel);
            }}
            leftIcon={<Pin className="w-4 h-4" />}
          >
            Add Insight
          </Button>
          <div className="h-4 w-px bg-surface-200" />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExportPdf}
            disabled={isExportingPdf}
            isLoading={isExportingPdf}
            leftIcon={!isExportingPdf ? <Download className="w-4 h-4" /> : undefined}
          >
            {isExportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button
            variant={report.sharing_config?.share_id ? "secondary" : "primary"}
            size="sm"
            onClick={() => setShowShareModal(true)}
            leftIcon={
              report.sharing_config?.share_id
                ? <Check className="w-4 h-4" />
                : <Share className="w-4 h-4" />
            }
          >
            {report.sharing_config?.share_id ? 'Shared' : 'Share'}
          </Button>
        </div>
      </div>

      {/* Conflict Resolution Banner */}
      {conflictState && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800">
              Someone else edited this report.{' '}
              {conflictDiffCount > 0
                ? `${conflictDiffCount} section${conflictDiffCount !== 1 ? 's' : ''} differ between your version and theirs.`
                : 'Your unsaved changes may conflict.'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismissConflict}
            >
              Dismiss
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLoadTheirChanges}>
              Use theirs
            </Button>
            <Button size="sm" onClick={handleKeepMyChanges}>
              Keep mine
            </Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b border-surface-200 bg-surface-50">
        {/* Add Section Buttons */}
        <div className="flex items-center gap-1 px-2 border-r border-surface-200">
          <button
            onClick={() => addSection('visualization')}
            className="p-2 text-surface-600 hover:text-surface-800 hover:bg-surface-100 rounded-sm transition-all cursor-pointer"
            title="Add Chart"
          >
            <BarChart3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => addSection('table')}
            className="p-2 text-surface-600 hover:text-surface-800 hover:bg-surface-100 rounded-sm transition-all cursor-pointer"
            title="Add Table"
          >
            <Table className="w-4 h-4" />
          </button>
        </div>

        {/* Text Formatting Buttons */}
        <div className="flex items-center gap-1 px-2 border-r border-surface-200">
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleBold().run()}
            isActive={isMarkActive('bold')}
            disabled={!activeEditor || isToolbarDisabled}
            title="Bold (⌘B)"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleItalic().run()}
            isActive={isMarkActive('italic')}
            disabled={!activeEditor || isToolbarDisabled}
            title="Italic (⌘I)"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleUnderline().run()}
            isActive={isMarkActive('underline')}
            disabled={!activeEditor || isToolbarDisabled}
            title="Underline (⌘U)"
          >
            <Underline className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleStrike().run()}
            isActive={isMarkActive('strike')}
            disabled={!activeEditor || isToolbarDisabled}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </ToolbarButton>
        </div>

        {/* Color & Font Size */}
        <div className="flex items-center gap-1 px-2 border-r border-surface-200">
          <div
            className={!activeEditor || isToolbarDisabled ? 'opacity-30 pointer-events-none' : ''}
            onMouseDown={(e) => e.preventDefault()}
          >
            <ColorPicker
              value={activeEditor?.getAttributes('textStyle').color}
              onChange={(color) => {
                if (!activeEditor) return;
                // Apply color to selection or set as active mark for next typed text
                if (color) {
                  activeEditor.chain().focus().setColor(color).run();
                } else {
                  activeEditor.chain().focus().unsetColor().run();
                }
                // Ensure editor regains focus after color picker closes
                setTimeout(() => activeEditor.commands.focus(), 0);
              }}
              label="Text Color"
              icon={<Type className="w-4 h-4" />}
            />
          </div>

          <div
            className={!activeEditor || isToolbarDisabled ? 'opacity-30 pointer-events-none' : ''}
            onMouseDown={(e) => e.preventDefault()}
          >
            <ColorPicker
              value={activeEditor?.getAttributes('highlight').color}
              onChange={(color) => {
                if (!activeEditor) return;
                // Apply highlight to selection or set as active mark for next typed text
                if (color) {
                  activeEditor.chain().focus().setHighlight({ color }).run();
                } else {
                  activeEditor.chain().focus().unsetHighlight().run();
                }
                // Ensure editor regains focus after color picker closes
                setTimeout(() => activeEditor.commands.focus(), 0);
              }}
              label="Highlight"
              icon={<Highlighter className="w-4 h-4" />}
            />
          </div>

          <div
            className={!activeEditor || isToolbarDisabled ? 'opacity-30 pointer-events-none' : ''}
            onMouseDown={(e) => e.preventDefault()}
          >
            <FontSizeDropdown
              value={activeEditor?.getAttributes('textStyle').fontSize}
              onChange={(size) => {
                if (!activeEditor) return;
                // Apply font size to selection or set as active mark for next typed text
                (activeEditor.chain().focus() as any).setFontSize(size).run();
                // Ensure editor regains focus after dropdown closes
                setTimeout(() => activeEditor.commands.focus(), 0);
              }}
            />
          </div>
        </div>

        {/* Heading & List Buttons */}
        <div className="flex items-center gap-1 px-2 border-r border-surface-200">
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={activeEditor?.isActive('heading', { level: 1 })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={activeEditor?.isActive('heading', { level: 2 })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={activeEditor?.isActive('heading', { level: 3 })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleBulletList().run()}
            isActive={activeEditor?.isActive('bulletList')}
            disabled={!activeEditor || isToolbarDisabled}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().toggleOrderedList().run()}
            isActive={activeEditor?.isActive('orderedList')}
            disabled={!activeEditor || isToolbarDisabled}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </ToolbarButton>
        </div>

        {/* Text Alignment */}
        <div className="flex items-center gap-1 px-2 border-r border-surface-200">
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().setTextAlign('left').run()}
            isActive={activeEditor?.isActive({ textAlign: 'left' })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Align Left"
          >
            <AlignLeft className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().setTextAlign('center').run()}
            isActive={activeEditor?.isActive({ textAlign: 'center' })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Align Center"
          >
            <AlignCenter className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().setTextAlign('right').run()}
            isActive={activeEditor?.isActive({ textAlign: 'right' })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Align Right"
          >
            <AlignRight className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().setTextAlign('justify').run()}
            isActive={activeEditor?.isActive({ textAlign: 'justify' })}
            disabled={!activeEditor || isToolbarDisabled}
            title="Justify"
          >
            <AlignJustify className="w-4 h-4" />
          </ToolbarButton>
        </div>

        {/* Clear Formatting */}
        <div className="flex items-center gap-1 px-2 border-r border-surface-200">
          <ToolbarButton
            onClick={() => activeEditor?.chain().focus().unsetAllMarks().clearNodes().run()}
            disabled={!activeEditor || isToolbarDisabled}
            title="Clear Formatting"
          >
            <RemoveFormatting className="w-4 h-4" />
          </ToolbarButton>
        </div>

        {/* Document-level Undo/Redo */}
        <div className="flex items-center gap-1 px-2">
          <ToolbarButton
            onClick={handleDocumentUndo}
            disabled={isToolbarDisabled || !history.canUndo}
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleDocumentRedo}
            disabled={isToolbarDisabled || !history.canRedo}
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </ToolbarButton>
        </div>

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleSave()}
          disabled={saveStatus === 'saving'}
          leftIcon={<Save className="w-4 h-4" />}
        >
          Save
        </Button>
      </div>

      {/* Main content area: editor canvas + optional side panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor Canvas */}
        <div
          ref={editorContainerRef}
          className="flex-1 overflow-y-auto bg-surface-100 relative"
          onDragOver={handleEditorDragOver}
          onDragLeave={handleEditorDragLeave}
          onDrop={handleEditorDrop}
        >
          {/* Scaled container - 75% zoom for better readability, centered */}
          <div style={{ zoom: 0.75 }}>
            {/* Single column layout - medium width, infinite scroll */}
            <div className="py-12 px-8 mx-auto" style={{ width: '1000px' }}>
              <div className={`bg-surface-0 border shadow-sm rounded-sm p-8 report-editor relative transition-all ${
                isDragOver ? 'border-primary-400 ring-2 ring-primary-200 ring-offset-2' : 'border-surface-200'
              }`}>
                {/* Single column container */}
                <div className="report-single-column">
                  <UnifiedReportContent
                    sections={sections}
                    reportId={report.id}
                    reportVersion={version}
                    onSectionUpdate={updateSectionContent}
                    onSectionDelete={deleteSection}
                    onSelect={setSelection}
                    onEditorReady={setActiveEditor}
                    onVersionUpdate={(newVersion) => {
                      setVersion(newVersion);
                      versionRef.current = newVersion;
                    }}
                    onRefetch={refetchReport}
                    onInsightDrop={handleInsightDrop}
                    isDraggingInsight={isDragOver}
                  />
                </div>

                {/* Add section button */}
                <button
                  onClick={() => addSection('text')}
                  className="w-full max-w-[800px] mx-auto py-4 mt-6 border-2 border-dashed border-surface-300 rounded-sm text-surface-500 hover:text-surface-600 hover:border-surface-400 transition-all flex items-center justify-center gap-2 text-sm cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Section</span>
                </button>
              </div>
            </div>
          </div>

          {/* Magic Edit Overlay */}
          <MagicEditOverlay
            selection={selection}
            isActive={magicEditActive}
            onClear={clearSelection}
            onActivate={() => setMagicEditActive(true)}
            onSubmit={handleMagicEditSubmit}
            suggestion={magicSuggestion}
            isLoading={magicEditLoading}
            onApply={handleMagicEditApply}
            onReject={handleMagicEditReject}
          />
        </div>

        {/* Side panels — push content, animated width */}
        <AnimatePresence>
          <motion.div
            key="ai-edit-panel"
            initial={false}
            animate={{ width: showAIEditPanel ? 440 : 0, opacity: showAIEditPanel ? 1 : 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="flex-shrink-0 overflow-hidden"
            style={{ pointerEvents: showAIEditPanel ? 'auto' : 'none' }}
          >
            <FullAIEditPanel
              isLoading={aiEditLoading}
              suggestion={aiEditSuggestion}
              error={aiEditError}
              onClose={() => setShowAIEditPanel(false)}
              onSubmit={handleAIEditSubmit}
              onApplySelected={handleAIEditApplySelected}
              onApplySingle={handleAIEditApplySingle}
              onRejectAll={handleAIEditRejectAll}
              undoSignal={undoSignal}
              initialPrompt={aiEditInitialPrompt}
            />
          </motion.div>
          {showAddInsightPanel && (
            <motion.div
              key="add-insight-panel"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 380, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="flex-shrink-0 overflow-hidden"
            >
              <AddInsightPanel
                isLoading={addInsightLoading}
                error={addInsightError}
                onClose={() => { setShowAddInsightPanel(false); setAddInsightError(null); }}
                onAdd={handleAddInsights}
                onAddByRef={handleAddInsightByRef}
                insightKeysInReport={insightKeysInReport}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Share Modal */}
      <ShareReportModal
        report={report}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        onSharingUpdated={(updatedReport) => {
          setReport(updatedReport);
        }}
      />

      {/* Success toast for add insight */}
      <AnimatePresence>
        {addInsightSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-surface-800 text-white px-4 py-2.5 rounded-sm shadow-lg text-sm font-medium flex items-center gap-2"
          >
            <Check className="w-4 h-4 text-emerald-400" />
            {addInsightSuccess}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Undo toast for AI edits */}
      <AnimatePresence>
        {undoToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-surface-800 text-white px-4 py-2.5 rounded-sm shadow-lg text-sm font-medium flex items-center gap-3"
          >
            <Check className="w-4 h-4 text-emerald-400" />
            <span>{undoToast}</span>
            <button
              type="button"
              onClick={handleUndoAIEdit}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-white/15 hover:bg-white/25 rounded-sm transition-colors text-sm font-medium cursor-pointer"
            >
              <Undo2 className="w-3.5 h-3.5" />
              Undo
            </button>
            <button
              type="button"
              onClick={() => {
                setUndoToast(null);
                setPreEditSections(null);
                if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
              }}
              className="p-0.5 hover:bg-white/15 rounded-sm transition-colors cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
